import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { 
  signInWithPhoneNumber, 
  RecaptchaVerifier, 
  ConfirmationResult,
  User as FirebaseUser,
  signOut,
  onAuthStateChanged
} from 'firebase/auth';
import { doc, setDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db } from '../firebase/config';
import { createUser, getUser } from '../firebase/firestore';
import { initializeNotifications, setupForegroundMessageListener } from '../services/notificationService';
import { AuthContextType, User, Address } from '../types';
import { setUserId } from '../utils/analytics';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

const cleanupAllRecaptcha = () => {
  const containers = document.querySelectorAll('[id*="recaptcha"]:not(#recaptcha-container)');
  containers.forEach(container => container.remove());
  
  if ((window as any).grecaptcha && (window as any).grecaptcha.reset) {
    try {
      (window as any).grecaptcha.reset();
    } catch (error) {
      console.warn('Warning resetting global reCAPTCHA:', error);
    }
  }
};

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [recaptchaVerifier, setRecaptchaVerifier] = useState<RecaptchaVerifier | null>(null);
  const [isRecaptchaReady, setIsRecaptchaReady] = useState<boolean>(false);

  useEffect(() => {
    if (!auth) {
      setTimeout(() => {
        setCurrentUser({
          id: 'demo-user',
          phone: '+91 9876543210',
          name: 'Demo User',
          email: 'demo@shetkarifresh.com',
          addresses: []
        });
        setLoading(false);
      }, 1000);
      return;
    }

    const unsubscribe = onAuthStateChanged(auth, async (user: FirebaseUser | null) => {
      if (user) {
        try {
          const userData = await getUser(user.uid);
          if (!userData) {
            try {
              await createUser({
                phone: user.phoneNumber || '',
                name: '',
                email: user.email || '',
                addresses: []
              });
              const newUserData = await getUser(user.uid);
              setCurrentUser(newUserData);
            } catch (createError: any) {
              console.warn('Failed to create user in Firestore (possibly offline):', createError);
              setCurrentUser({
                id: user.uid,
                phone: user.phoneNumber || '',
                name: user.displayName || '',
                email: user.email || '',
                addresses: []
              });
            }
          } else {
            setCurrentUser(userData);
          }
          
          // Set user ID for analytics (no PII)
          setUserId(user.uid);
          
          try {
            await initializeNotifications(user.uid);
            setupForegroundMessageListener();
          } catch (error: any) {
            console.warn('⚠️ Push notifications not available:', error.message);
          }
        } catch (error: any) {
          console.warn('Error fetching user data (possibly offline):', error);
          setCurrentUser({
            id: user.uid,
            phone: user.phoneNumber || '',
            name: user.displayName || '',
            email: user.email || '',
            addresses: []
          });
          // Set user ID for analytics even if Firestore fails
          setUserId(user.uid);
        }
      } else {
        setCurrentUser(null);
        // Clear user ID on logout
        setUserId(null);
      }
      setLoading(false);
    });

    return () => {
      unsubscribe();
      
      if (recaptchaVerifier) {
        try {
          recaptchaVerifier.clear();
        } catch (error) {
          console.warn('Warning cleaning up reCAPTCHA:', error);
        }
      }
      
      const container = document.getElementById('recaptcha-container');
      if (container) {
        container.remove();
      }
    };
  }, [recaptchaVerifier]);

  useEffect(() => {
    if (!auth) return;
  }, [auth]);

  const sendOTP = async (phoneNumber: string): Promise<ConfirmationResult | { phoneNumber: string }> => {
    if (!auth) {
      throw new Error('Firebase not initialized. Check your environment variables.');
    }
    
    try {
      if (recaptchaVerifier) {
        try {
          await new Promise(resolve => setTimeout(resolve, 200));
          recaptchaVerifier.clear();
        } catch (e) {
          console.warn('Error clearing verifier:', e);
        }
        setRecaptchaVerifier(null);
      }
      
      const existingContainer = document.getElementById('recaptcha-container');
      if (existingContainer) {
        existingContainer.style.display = 'none';
        setTimeout(() => {
          if (existingContainer.parentNode) {
            existingContainer.remove();
          }
        }, 100);
        await new Promise(resolve => setTimeout(resolve, 150));
      }
      
      const container = document.createElement('div');
      container.id = 'recaptcha-container';
      container.style.cssText = `
        position: fixed !important;
        bottom: 20px !important;
        right: 20px !important;
        z-index: 10000 !important;
        background: white !important;
        padding: 16px !important;
        border-radius: 8px !important;
        box-shadow: 0 4px 12px rgba(0,0,0,0.15) !important;
        border: 2px solid #22c55e !important;
      `;
      
      document.body.appendChild(container);
      
      const labelContainer = document.createElement('div');
      labelContainer.id = 'recaptcha-label';
      labelContainer.style.cssText = `
        position: fixed !important;
        bottom: 120px !important;
        right: 20px !important;
        z-index: 10001 !important;
        background: #22c55e !important;
        color: white !important;
        padding: 8px 12px !important;
        border-radius: 4px !important;
        font-size: 12px !important;
        font-weight: 600 !important;
        box-shadow: 0 2px 8px rgba(0,0,0,0.1) !important;
      `;
      labelContainer.textContent = 'Security verification in progress...';
      document.body.appendChild(labelContainer);
      
      await new Promise(resolve => setTimeout(resolve, 100));
      
      let verifier;
      try {
        verifier = new RecaptchaVerifier(auth, 'recaptcha-container', {
          size: 'invisible',
          callback: () => {},
          'expired-callback': () => {
            console.warn('⚠️ reCAPTCHA expired');
          }
        });
      } catch (invisibleError) {
        console.warn('Invisible reCAPTCHA failed, trying normal:', invisibleError);
        verifier = new RecaptchaVerifier(auth, 'recaptcha-container', {
          size: 'normal',
          callback: () => {},
          'expired-callback': () => {
            console.warn('⚠️ reCAPTCHA expired');
          }
        });
      }
      
      const result = await signInWithPhoneNumber(auth, phoneNumber, verifier);
      
      container.style.display = 'none';
      const label = document.getElementById('recaptcha-label');
      if (label) {
        label.textContent = 'Verification complete!';
        setTimeout(() => label.remove(), 2000);
      }
      
      setRecaptchaVerifier(verifier);
      return result;
      
    } catch (error: any) {
      console.error('❌ Error sending OTP:', error);
      
      const errorContainer = document.getElementById('recaptcha-container');
      if (errorContainer) {
        errorContainer.style.display = 'none';
        setTimeout(() => {
          if (errorContainer.parentNode) {
            errorContainer.remove();
          }
        }, 1000);
      }
      const errorLabel = document.getElementById('recaptcha-label');
      if (errorLabel) {
        errorLabel.textContent = 'Verification failed';
        errorLabel.style.background = '#ef4444';
        setTimeout(() => errorLabel.remove(), 3000);
      }
      
      if (error.code === 'auth/argument-error') {
        throw new Error('reCAPTCHA configuration error. Please refresh the page and try again. If the issue persists, check Firebase Console settings.');
      } else if (error.code === 'auth/invalid-app-credential') {
        throw new Error('Firebase phone authentication not enabled. Go to Firebase Console → Authentication → Sign-in method → Enable Phone provider.');
      } else if (error.code === 'auth/captcha-check-failed') {
        throw new Error('reCAPTCHA verification failed. Please try again.');
      } else if (error.code === 'auth/invalid-phone-number') {
        throw new Error('Invalid phone number format. Use format: +919876543210');
      } else if (error.code === 'auth/too-many-requests') {
        throw new Error('Too many requests. Please try again later.');
      }
      
      throw new Error(`Authentication failed: ${error.message}`);
    }
  };

  const verifyOTP = async (confirmationResult: any, otp: string): Promise<FirebaseUser | { uid: string; phoneNumber: string }> => {
    if (!auth) {
      throw new Error('Firebase not initialized. Check your environment variables.');
    }
    
    try {
      const result = await confirmationResult.confirm(otp);
      const user = result.user;
      
      if (db && user) {
        const userRef = doc(db, 'users', user.uid);
        
        try {
          const existingUser = await getUser(user.uid);
          
          if (existingUser) {
            await updateDoc(userRef, {
              phone: user.phoneNumber || existingUser.phone,
              updatedAt: serverTimestamp()
            });
            setCurrentUser(existingUser);
          } else {
            const newUserData = {
              id: user.uid,
              phone: user.phoneNumber || '',
              name: user.displayName || '',
              email: user.email || '',
              addresses: [],
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString()
            };
            await setDoc(userRef, newUserData);
            setCurrentUser(newUserData);
          }
        } catch (firestoreError) {
          console.warn('Failed to save user to Firestore:', firestoreError);
          const fallbackData = {
            id: user.uid,
            phone: user.phoneNumber || '',
            name: user.displayName || '',
            email: user.email || '',
            addresses: []
          };
          setCurrentUser(fallbackData);
        }
      } else {
        const userData = {
          id: user.uid,
          phone: user.phoneNumber || '',
          name: user.displayName || '',
          email: user.email || '',
          addresses: []
        };
        setCurrentUser(userData);
      }
      
      return user;
    } catch (error: any) {
      console.error('Error verifying OTP:', error);
      
      if (error.code === 'auth/invalid-verification-code') {
        throw new Error('Invalid OTP. Please check and try again.');
      } else if (error.code === 'auth/code-expired') {
        throw new Error('OTP has expired. Please request a new one.');
      }
      
      throw new Error('OTP verification failed. Please try again.');
    }
  };

  const logout = async (): Promise<void> => {
    if (!auth) return;
    
    try {
      await signOut(auth);
      setCurrentUser(null);
    } catch (error) {
      console.error('Error signing out:', error);
      throw error;
    }
  };

  const addAddress = async (address: Address): Promise<boolean> => {
    if (!currentUser || !db) return false;
    
    try {
      const updatedAddresses = [...currentUser.addresses];
      
      if (address.isDefault) {
        updatedAddresses.forEach(addr => addr.isDefault = false);
      }
      
      updatedAddresses.push(address);
      
      const userRef = doc(db, 'users', currentUser.id);
      await updateDoc(userRef, { addresses: updatedAddresses.map(addr => ({
        id: addr.id || '',
        title: addr.title || '',
        name: addr.name || '',
        phone: addr.phone || '',
        wingFlatNumber: addr.wingFlatNumber || '',
        area: addr.area || '',
        fullAddress: addr.fullAddress || '',
        city: addr.city || '',
        state: addr.state || '',
        pincode: addr.pincode || '',
        latitude: addr.latitude || 0,
        longitude: addr.longitude || 0,
        isDefault: addr.isDefault || false,
      })) });
      
      setCurrentUser({
        ...currentUser,
        addresses: updatedAddresses
      });
      
      return true;
    } catch (error) {
      console.error('Error adding address:', error);
      return false;
    }
  };

  const updateAddress = async (address: Address): Promise<boolean> => {
    if (!currentUser || !db) return false;
    
    try {
      const updatedAddresses = currentUser.addresses.map(addr => {
        if (addr.id === address.id) {
          return address;
        }
        if (address.isDefault && addr.id !== address.id) {
          return { ...addr, isDefault: false };
        }
        return addr;
      });
      
      const userRef = doc(db, 'users', currentUser.id);
      await updateDoc(userRef, { addresses: updatedAddresses.map(addr => ({
        id: addr.id,
        title: addr.title,
        name: addr.name || '',
        phone: addr.phone || '',
        wingFlatNumber: addr.wingFlatNumber,
        area: addr.area,
        fullAddress: addr.fullAddress,
        city: addr.city,
        state: addr.state,
        pincode: addr.pincode,
        latitude: addr.latitude,
        longitude: addr.longitude,
        isDefault: addr.isDefault,
      })) });
      
      setCurrentUser({
        ...currentUser,
        addresses: updatedAddresses
      });
      
      return true;
    } catch (error) {
      console.error('Error updating address:', error);
      return false;
    }
  };

  const deleteAddress = async (addressId: string): Promise<boolean> => {
    if (!currentUser || !db) return false;
    
    try {
      const updatedAddresses = currentUser.addresses.filter(addr => addr.id !== addressId);
      
      const userRef = doc(db, 'users', currentUser.id);
      await updateDoc(userRef, { addresses: updatedAddresses.map(addr => ({
        id: addr.id,
        title: addr.title,
        name: addr.name || '',
        phone: addr.phone || '',
        wingFlatNumber: addr.wingFlatNumber,
        area: addr.area,
        fullAddress: addr.fullAddress,
        city: addr.city,
        state: addr.state,
        pincode: addr.pincode,
        latitude: addr.latitude,
        longitude: addr.longitude,
        isDefault: addr.isDefault,
      })) });
      
      setCurrentUser({
        ...currentUser,
        addresses: updatedAddresses
      });
      
      return true;
    } catch (error) {
      console.error('Error deleting address:', error);
      return false;
    }
  };

  const setDefaultAddress = async (addressId: string): Promise<boolean> => {
    if (!currentUser || !db) return false;
    
    try {
      const updatedAddresses = currentUser.addresses.map(addr => ({
        ...addr,
        isDefault: addr.id === addressId
      }));
      
      const userRef = doc(db, 'users', currentUser.id);
      await updateDoc(userRef, { addresses: updatedAddresses.map(addr => ({
        id: addr.id,
        title: addr.title,
        name: addr.name || '',
        phone: addr.phone || '',
        wingFlatNumber: addr.wingFlatNumber,
        area: addr.area,
        fullAddress: addr.fullAddress,
        city: addr.city,
        state: addr.state,
        pincode: addr.pincode,
        latitude: addr.latitude,
        longitude: addr.longitude,
        isDefault: addr.isDefault
      })) });
      
      setCurrentUser({
        ...currentUser,
        addresses: updatedAddresses
      });
      
      return true;
    } catch (error) {
      console.error('Error setting default address:', error);
      return false;
    }
  };

  const updateProfile = async (profileData: { name?: string; email?: string }): Promise<boolean> => {
    if (!currentUser || !db) return false;
    
    try {
      const userRef = doc(db, 'users', currentUser.id);
      await updateDoc(userRef, {
        ...profileData,
        updatedAt: serverTimestamp()
      });
      
      setCurrentUser({
        ...currentUser,
        ...profileData
      });
      
      return true;
    } catch (error) {
      console.error('Error updating profile:', error);
      return false;
    }
  };

  const refreshUser = async (): Promise<void> => {
    if (!currentUser || !db) return;
    
    try {
      const userData = await getUser(currentUser.id);
      if (userData) {
        setCurrentUser(userData);
      }
    } catch (error) {
      console.error('Error refreshing user:', error);
    }
  };

  const value = {
    currentUser,
    sendOTP,
    verifyOTP,
    logout,
    loading,
    addAddress,
    updateAddress,
    deleteAddress,
    setDefaultAddress,
    updateProfile,
    refreshUser
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};
